import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";
import {
  CONSULT_STATUSES, MEETING_TYPES, TIME_ZONES, SERVICE_LANES, REVENUE_TYPES,
  NOTES_TEMPLATE, labelOf, optOf,
} from "@/config/consultOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PopoutDialog } from "@/components/ui/PopoutDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarHeart, Plus, Search, Phone, Video, MapPin, Mail, Sparkles, FileText,
  UserCheck, Route, ListPlus, Send, FolderInput, Handshake, DollarSign, MoonStar,
  Save, ClipboardList, CheckCircle2, X,
} from "lucide-react";
import { toast } from "sonner";

const TONE = {
  red: { bg: "rgba(156,30,34,0.10)", color: "#9C1E22", border: "rgba(156,30,34,0.24)" },
  amber: { bg: "rgba(183,121,31,0.14)", color: "#9A6516", border: "rgba(183,121,31,0.30)" },
  sky: { bg: "rgba(155,185,206,0.25)", color: "#1C4358", border: "rgba(155,185,206,0.45)" },
  navy: { bg: "rgba(28,67,88,0.10)", color: "#1C4358", border: "rgba(28,67,88,0.20)" },
  green: { bg: "rgba(47,125,90,0.12)", color: "#256B4B", border: "rgba(47,125,90,0.28)" },
  gray: { bg: "rgba(75,85,99,0.10)", color: "#4B5563", border: "rgba(75,85,99,0.22)" },
};
const ConsultBadge = ({ value }) => {
  const o = optOf(CONSULT_STATUSES, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }}>{o?.label || value || "—"}</span>
  );
};
const MeetingIcon = ({ type, ...p }) => {
  const C = type === "phone" ? Phone : type === "in_person" ? MapPin : Video;
  return <C {...p} />;
};

const splitDT = (iso) => {
  if (!iso) return { date: "", time: "10:00" };
  const [d, t] = String(iso).split("T");
  return { date: d || "", time: (t || "10:00").slice(0, 5) };
};
const combineDT = (date, time) => (date ? `${date}T${time || "10:00"}:00` : null);

const newConsultEmpty = {
  lead_id: "", family_name: "", loved_one_name: "", consultant_user_id: "",
  date: "", time: "10:00", time_zone: "America/Chicago", meeting_type: "video",
  meeting_link: "", main_issue: "", service_lane: "", status: "scheduled",
  notes_before: "", branch_id: "",
};

export default function Consultations() {
  const { user } = useAuth();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("consultations");

  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [partners, setPartners] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "all", consultant_user_id: "all", service_lane: "all", branch_id: "all" });

  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState(newConsultEmpty);
  const [creating, setCreating] = useState(false);

  const [wsOpen, setWsOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [wsForm, setWsForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [actionType, setActionType] = useState(null);
  const [actionForm, setActionForm] = useState({});
  const [actionBusy, setActionBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const staffName = (id) => staff.find((s) => s.id === id)?.full_name || "—";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      ["status", "consultant_user_id", "service_lane", "branch_id"].forEach((k) => {
        if (filters[k] && filters[k] !== "all") params[k] = filters[k];
      });
      const { data } = await api.get("/consultations", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data.items || [])).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data.items || [])).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data.items || [])).catch(() => {});
    api.get("/leads").then((r) => setLeads((r.data.items || []).filter((l) => l.status !== "converted"))).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1" && canWrite) {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canWrite]);

  // ---- New consultation ----
  const openNew = () => {
    setNewForm({ ...newConsultEmpty, consultant_user_id: user?.id || "", branch_id: branches.length === 1 ? branches[0].id : "" });
    setNewOpen(true);
  };
  const onPickLead = (lead_id) => {
    const l = leads.find((x) => x.id === lead_id);
    setNewForm((p) => ({
      ...p, lead_id,
      family_name: l ? [l.first_name, l.last_name].filter(Boolean).join(" ") || l.name || "" : p.family_name,
      loved_one_name: l?.loved_one_name || p.loved_one_name,
      service_lane: l?.service_interest || p.service_lane,
      branch_id: l?.branch_id || p.branch_id,
    }));
  };
  const createConsult = async (e) => {
    e.preventDefault();
    if (!newForm.family_name) { toast.error("Family name is required"); return; }
    setCreating(true);
    try {
      const payload = {
        lead_id: newForm.lead_id || undefined,
        family_name: newForm.family_name, loved_one_name: newForm.loved_one_name,
        consultant_user_id: newForm.consultant_user_id || undefined,
        scheduled_at: combineDT(newForm.date, newForm.time), time_zone: newForm.time_zone,
        meeting_type: newForm.meeting_type, mode: newForm.meeting_type, meeting_link: newForm.meeting_link,
        main_issue: newForm.main_issue, service_lane: newForm.service_lane, status: newForm.status,
        notes_before: newForm.notes_before,
      };
      if (newForm.branch_id) payload.branch_id = newForm.branch_id;
      const { data } = await api.post("/consultations", payload);
      toast.success("Consultation scheduled");
      setNewOpen(false);
      await load();
      openWorkspace(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create consultation");
    } finally { setCreating(false); }
  };

  // ---- Workspace ----
  const openWorkspace = async (c) => {
    setActive(c);
    const dt = splitDT(c.scheduled_at);
    setWsForm({
      status: c.status || "scheduled", date: dt.date, time: dt.time,
      time_zone: c.time_zone || "America/Chicago", meeting_type: c.meeting_type || c.mode || "video",
      meeting_link: c.meeting_link || "", consultant_user_id: c.consultant_user_id || "",
      main_issue: c.main_issue || "", service_lane: c.service_lane || "",
      recommended_next_step: c.recommended_next_step || "",
      notes_before: c.notes_before || "", notes_after: c.notes_after || "",
    });
    setActiveLead(null);
    setWsOpen(true);
    if (c.lead_id) {
      try { const r = await api.get(`/leads/${c.lead_id}`); setActiveLead(r.data); } catch (e) {}
    }
  };
  const wsSet = (k, v) => setWsForm((p) => ({ ...p, [k]: v }));

  const saveWorkspace = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const payload = {
        status: wsForm.status, scheduled_at: combineDT(wsForm.date, wsForm.time),
        time_zone: wsForm.time_zone, meeting_type: wsForm.meeting_type, mode: wsForm.meeting_type,
        meeting_link: wsForm.meeting_link, consultant_user_id: wsForm.consultant_user_id || undefined,
        main_issue: wsForm.main_issue, service_lane: wsForm.service_lane,
        recommended_next_step: wsForm.recommended_next_step,
        notes_before: wsForm.notes_before, notes_after: wsForm.notes_after,
      };
      const { data } = await api.put(`/consultations/${active.id}`, payload);
      setActive(data);
      toast.success("Consultation saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const insertTemplate = () => {
    if (wsForm.notes_after && wsForm.notes_after.trim()) {
      wsSet("notes_after", `${wsForm.notes_after}\n\n${NOTES_TEMPLATE}`);
    } else {
      wsSet("notes_after", NOTES_TEMPLATE);
    }
    toast.success("Template inserted into call notes");
  };

  // ---- Actions ----
  const runAction = async (body) => {
    setActionBusy(true);
    try {
      const { data } = await api.post(`/consultations/${active.id}/action`, body);
      setActive(data.consultation);
      // sync editable bits that may have changed
      setWsForm((p) => ({ ...p, status: data.consultation.status, service_lane: data.consultation.service_lane }));
      await load();
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
      throw err;
    } finally { setActionBusy(false); }
  };

  const ACTIONS = [
    { key: "convert", label: "Convert to Family Case", icon: UserCheck, tone: "red", disabled: () => !!active?.case_id || !active?.lead_id },
    { key: "assign_lane", label: "Assign Service Lane", icon: Route, tone: "navy" },
    { key: "follow_up_task", label: "Create Follow-up Task", icon: ListPlus, tone: "navy" },
    { key: "follow_up_email", label: "Send Follow-up Email", icon: Send, tone: "navy" },
    { key: "request_documents", label: "Request Documents", icon: FolderInput, tone: "navy" },
    { key: "assign_partner", label: "Assign Partner / Vendor", icon: Handshake, tone: "navy" },
    { key: "revenue_opportunity", label: "Create Revenue Opportunity", icon: DollarSign, tone: "green" },
    { key: "nurture", label: "Move to Nurture", icon: MoonStar, tone: "amber" },
  ];

  const openAction = async (key) => {
    if (key === "convert") {
      try { const d = await runAction({ action: "convert" }); toast.success(`Converted to case ${d.case?.case_number || ""}`); } catch (e) {}
      return;
    }
    if (key === "nurture") {
      try { await runAction({ action: "nurture" }); toast.success("Moved to nurture"); } catch (e) {}
      return;
    }
    // dialog-based actions
    const defaults = {
      assign_lane: { service_lane: active?.service_lane || "" },
      follow_up_task: { title: "", due_date: "", priority: "medium" },
      request_documents: { note: "" },
      assign_partner: { partner_id: "", referral_type: active?.service_lane || "" },
      revenue_opportunity: { amount: "", revenue_type: "service_fee", service_lane: active?.service_lane || "" },
      follow_up_email: { to: activeLead?.email || "", subject: "Following up on your free consultation", body: "" },
    };
    setActionForm(defaults[key] || {});
    setActionType(key);
  };

  const aiDraftEmail = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/draft-followup", {
        purpose: "Follow up after the free one-hour family transition consultation",
        related_type: "consultations", related_id: active.id,
        context: `Family: ${active.family_name}. Notes: ${wsForm.notes_after || active.notes_after || ""}. Next step: ${wsForm.recommended_next_step || ""}`,
      });
      setActionForm((p) => ({ ...p, body: data.result }));
    } catch (e) {
      toast.error("AI draft is unavailable right now");
    } finally { setAiBusy(false); }
  };

  const submitAction = async (e) => {
    e.preventDefault();
    const f = actionForm;
    let body;
    if (actionType === "assign_lane") {
      if (!f.service_lane) { toast.error("Choose a service lane"); return; }
      body = { action: "assign_lane", service_lane: f.service_lane };
    } else if (actionType === "follow_up_task") {
      body = { action: "follow_up_task", title: f.title || undefined, due_date: f.due_date || undefined, priority: f.priority };
    } else if (actionType === "request_documents") {
      body = { action: "request_documents", note: f.note };
    } else if (actionType === "assign_partner") {
      if (!f.partner_id) { toast.error("Choose a partner"); return; }
      body = { action: "assign_partner", partner_id: f.partner_id, referral_type: f.referral_type };
    } else if (actionType === "revenue_opportunity") {
      body = { action: "revenue_opportunity", amount: Number(f.amount) || 0, revenue_type: f.revenue_type, service_lane: f.service_lane };
    } else if (actionType === "follow_up_email") {
      body = { action: "follow_up_email", subject: f.subject, body: f.body };
    }
    try {
      await runAction(body);
      if (actionType === "follow_up_email" && f.to) {
        window.location.href = `mailto:${f.to}?subject=${encodeURIComponent(f.subject || "")}&body=${encodeURIComponent(f.body || "")}`;
      }
      toast.success("Done");
      setActionType(null);
    } catch (e) {}
  };

  const FilterSelect = ({ value, onChange, options, testid, all }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-full sm:w-[150px]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">{all}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const leadName = (l) => [l.first_name, l.last_name].filter(Boolean).join(" ") || l.name || "Lead";

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2">
            <CalendarHeart size={22} className="text-[#1C4358]" /> Consultations
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">The free one-hour family transition consultation — your main conversion point.</p>
        </div>
        {canWrite && (
          <Button onClick={openNew} data-testid="new-consultation-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Plus size={17} className="mr-1.5" /> New Consultation
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Search family, loved one, notes..." className="pl-9 h-9" data-testid="consult-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:flex gap-2">
            <FilterSelect value={filters.status} onChange={(v) => setFilters((p) => ({ ...p, status: v }))} options={CONSULT_STATUSES} testid="filter-status" all="All status" />
            <FilterSelect value={filters.consultant_user_id} onChange={(v) => setFilters((p) => ({ ...p, consultant_user_id: v }))} options={staff.map((s) => ({ value: s.id, label: s.full_name }))} testid="filter-consultant" all="Any consultant" />
            <FilterSelect value={filters.service_lane} onChange={(v) => setFilters((p) => ({ ...p, service_lane: v }))} options={SERVICE_LANES} testid="filter-lane" all="All lanes" />
            <FilterSelect value={filters.branch_id} onChange={(v) => setFilters((p) => ({ ...p, branch_id: v }))} options={branches.map((b) => ({ value: b.id, label: b.name }))} testid="filter-branch" all="All branches" />
          </div>
        </div>
      </Card>

      {/* List */}
      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
              <CalendarHeart size={26} className="text-[#1C4358]" />
            </div>
            <p className="font-medium text-[#252221]">No consultations found</p>
            <p className="text-sm text-muted-foreground">Schedule a free consultation to start the conversion.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="consultations-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Family</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">When</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Consultant</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Outcome</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} className="hover:bg-[rgba(155,185,206,0.10)] cursor-pointer" data-testid="consult-row" onClick={() => openWorkspace(c)}>
                    <TableCell className="py-3">
                      <div className="font-semibold text-[#252221]">{c.family_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{c.loved_one_name ? `For ${c.loved_one_name}` : ""}{c.service_lane ? `${c.loved_one_name ? " · " : ""}${labelOf(SERVICE_LANES, c.service_lane)}` : ""}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1.5 text-[#252221]"><MeetingIcon type={c.meeting_type || c.mode} size={14} className="text-[#1C4358]" /> {c.scheduled_at ? formatDateTime(c.scheduled_at) : "Not set"}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{staffName(c.consultant_user_id)}</TableCell>
                    <TableCell><ConsultBadge value={c.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {c.case_created && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(47,125,90,0.12)", color: "#256B4B" }}><CheckCircle2 size={11} /> Case</span>}
                        {c.revenue_opportunity_created && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(183,121,31,0.14)", color: "#9A6516" }}><DollarSign size={11} /> Revenue</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openWorkspace(c); }} data-testid="open-workspace-button">Open</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">{items.length} consultation{items.length === 1 ? "" : "s"}</p>

      {/* New consultation dialog — modal by default, detachable to a floating pop-out */}
      <PopoutDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        title="Schedule Free Consultation"
        subtitle="Consultations"
        icon={CalendarHeart}
        maxWidthClass="max-w-xl"
        testid="new-consult-dialog"
      >
        <form onSubmit={createConsult} className="space-y-4">
          <p className="text-sm text-muted-foreground -mt-1">Book the free one-hour family transition consultation.</p>
            <div><Label className="mb-1.5 block text-[13px]">Link a lead (optional)</Label>
              <Select value={newForm.lead_id} onValueChange={onPickLead}>
                <SelectTrigger data-testid="consult-lead"><SelectValue placeholder="Search active leads" /></SelectTrigger>
                <SelectContent className="max-h-[260px]">{leads.map((l) => <SelectItem key={l.id} value={l.id}>{leadName(l)}{l.loved_one_name ? ` · ${l.loved_one_name}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">Family name *</Label><Input value={newForm.family_name} onChange={(e) => setNewForm((p) => ({ ...p, family_name: e.target.value }))} data-testid="consult-family-name" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Loved one's name</Label><Input value={newForm.loved_one_name} onChange={(e) => setNewForm((p) => ({ ...p, loved_one_name: e.target.value }))} data-testid="consult-loved-one" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Date</Label><Input type="date" value={newForm.date} onChange={(e) => setNewForm((p) => ({ ...p, date: e.target.value }))} data-testid="consult-new-date" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="mb-1.5 block text-[13px]">Time</Label><Input type="time" value={newForm.time} onChange={(e) => setNewForm((p) => ({ ...p, time: e.target.value }))} /></div>
                <div><Label className="mb-1.5 block text-[13px]">Zone</Label>
                  <Select value={newForm.time_zone} onValueChange={(v) => setNewForm((p) => ({ ...p, time_zone: v }))}>
                    <SelectTrigger data-testid="consult-tz"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIME_ZONES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Meeting type</Label>
                <Select value={newForm.meeting_type} onValueChange={(v) => setNewForm((p) => ({ ...p, meeting_type: v }))}>
                  <SelectTrigger data-testid="consult-meeting-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{MEETING_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Consultant</Label>
                <Select value={newForm.consultant_user_id} onValueChange={(v) => setNewForm((p) => ({ ...p, consultant_user_id: v }))}>
                  <SelectTrigger data-testid="consult-consultant"><SelectValue placeholder="Assign" /></SelectTrigger>
                  <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Service lane</Label>
                <Select value={newForm.service_lane} onValueChange={(v) => setNewForm((p) => ({ ...p, service_lane: v }))}>
                  <SelectTrigger data-testid="consult-lane-new"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {branches.length > 1 && (
                <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Branch</Label>
                  <Select value={newForm.branch_id} onValueChange={(v) => setNewForm((p) => ({ ...p, branch_id: v }))}>
                    <SelectTrigger data-testid="consult-branch-new"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2"><Label className="mb-1.5 block text-[13px]">Notes before meeting</Label><Textarea rows={2} value={newForm.notes_before} onChange={(e) => setNewForm((p) => ({ ...p, notes_before: e.target.value }))} data-testid="consult-notes-before-new" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="consult-create-button">{creating ? "Scheduling..." : "Schedule"}</Button>
            </div>
        </form>
      </PopoutDialog>

      {/* Workspace */}
      <Dialog open={wsOpen} onOpenChange={setWsOpen}>
        <DialogContent className="max-w-3xl max-h-[94vh] overflow-y-auto mmm-scroll" data-testid="consult-workspace">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-3 pr-6">
                  <div>
                    <DialogTitle className="font-display text-xl text-[#252221]">{active.family_name}</DialogTitle>
                    <DialogDescription>
                      {active.loved_one_name ? `For ${active.loved_one_name} · ` : ""}{active.scheduled_at ? formatDateTime(active.scheduled_at) : "Not scheduled"}
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeLead?.phone && <a href={`tel:${activeLead.phone}`} className="p-1.5 rounded-lg text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" title="Call"><Phone size={16} /></a>}
                    {activeLead?.email && <a href={`mailto:${activeLead.email}`} className="p-1.5 rounded-lg text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" title="Email"><Mail size={16} /></a>}
                    <ConsultBadge value={active.status} />
                  </div>
                </div>
              </DialogHeader>

              {/* Outcome flags */}
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "#E6EDF2", color: active.case_created ? "#256B4B" : "#6B7280", backgroundColor: active.case_created ? "rgba(47,125,90,0.10)" : "#F7FAFC" }} data-testid="flag-case-created">
                  <CheckCircle2 size={13} /> Case created: {active.case_created ? "Yes" : "No"}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "#E6EDF2", color: active.revenue_opportunity_created ? "#9A6516" : "#6B7280", backgroundColor: active.revenue_opportunity_created ? "rgba(183,121,31,0.12)" : "#F7FAFC" }} data-testid="flag-revenue-created">
                  <DollarSign size={13} /> Revenue opportunity: {active.revenue_opportunity_created ? "Yes" : "No"}
                </span>
              </div>

              {/* Editable details */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><Label className="mb-1.5 block text-[13px]">Status</Label>
                  <Select value={wsForm.status} onValueChange={(v) => wsSet("status", v)}>
                    <SelectTrigger data-testid="ws-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{CONSULT_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Date</Label><Input type="date" value={wsForm.date} onChange={(e) => wsSet("date", e.target.value)} data-testid="ws-date" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="mb-1.5 block text-[13px]">Time</Label><Input type="time" value={wsForm.time} onChange={(e) => wsSet("time", e.target.value)} /></div>
                  <div><Label className="mb-1.5 block text-[13px]">Zone</Label>
                    <Select value={wsForm.time_zone} onValueChange={(v) => wsSet("time_zone", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TIME_ZONES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Meeting type</Label>
                  <Select value={wsForm.meeting_type} onValueChange={(v) => wsSet("meeting_type", v)}>
                    <SelectTrigger data-testid="ws-meeting-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{MEETING_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Consultant</Label>
                  <Select value={wsForm.consultant_user_id} onValueChange={(v) => wsSet("consultant_user_id", v)}>
                    <SelectTrigger data-testid="ws-consultant"><SelectValue placeholder="Assign" /></SelectTrigger>
                    <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-[13px]">Service lane</Label>
                  <Select value={wsForm.service_lane} onValueChange={(v) => wsSet("service_lane", v)}>
                    <SelectTrigger data-testid="ws-lane"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Main issue</Label><Input value={wsForm.main_issue} onChange={(e) => wsSet("main_issue", e.target.value)} placeholder="The family's primary concern" data-testid="ws-main-issue" /></div>

              {/* Notes */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-[13px]">Notes before meeting</Label>
                  <Textarea rows={4} value={wsForm.notes_before} onChange={(e) => wsSet("notes_before", e.target.value)} data-testid="ws-notes-before" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="block text-[13px]">Call notes (after)</Label>
                    <button type="button" onClick={insertTemplate} className="text-xs text-[#1C4358] hover:underline flex items-center gap-1" data-testid="insert-template-button"><ClipboardList size={13} /> Insert template</button>
                  </div>
                  <Textarea rows={4} value={wsForm.notes_after} onChange={(e) => wsSet("notes_after", e.target.value)} placeholder="Capture answers during/right after the call" data-testid="ws-notes-after" />
                </div>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Recommended next step</Label><Input value={wsForm.recommended_next_step} onChange={(e) => wsSet("recommended_next_step", e.target.value)} placeholder="e.g. Convert to case and assign placement lane" data-testid="ws-next-step" /></div>

              <div className="flex justify-end">
                <Button onClick={saveWorkspace} disabled={saving} className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }} data-testid="ws-save-button"><Save size={15} className="mr-1.5" /> {saving ? "Saving..." : "Save consultation"}</Button>
              </div>

              {/* Post-consultation actions */}
              <div className="rounded-xl border border-[#E6EDF2] p-4" style={{ backgroundColor: "#F7FAFC" }}>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={16} className="text-[#9C1E22]" />
                  <h4 className="font-display font-semibold text-[#252221]">After the consultation</h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {ACTIONS.map((a) => {
                    const disabled = (a.disabled ? a.disabled() : false) || actionBusy;
                    const t = TONE[a.tone];
                    return (
                      <button key={a.key} disabled={disabled} onClick={() => openAction(a.key)} data-testid={`action-${a.key}`}
                        className="flex items-center gap-2 rounded-lg border border-[#E6EDF2] bg-white px-3 py-2.5 text-left text-sm font-medium text-[#252221] hover:shadow-[var(--mmm-shadow-md)] transition-shadow disabled:opacity-40 disabled:cursor-not-allowed">
                        <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: t.bg }}><a.icon size={15} style={{ color: t.color }} /></span>
                        <span className="leading-tight">{a.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action sub-dialog */}
      <Dialog open={!!actionType} onOpenChange={(o) => !o && setActionType(null)}>
        <DialogContent className="max-w-md" data-testid="action-dialog">
          {actionType && (
            <form onSubmit={submitAction} className="space-y-4">
              <DialogHeader>
                <DialogTitle className="font-display text-lg">{ACTIONS.find((a) => a.key === actionType)?.label}</DialogTitle>
              </DialogHeader>

              {actionType === "assign_lane" && (
                <div><Label className="mb-1.5 block text-[13px]">Service lane</Label>
                  <Select value={actionForm.service_lane} onValueChange={(v) => setActionForm((p) => ({ ...p, service_lane: v }))}>
                    <SelectTrigger data-testid="action-lane-select"><SelectValue placeholder="Choose lane" /></SelectTrigger>
                    <SelectContent>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              {actionType === "follow_up_task" && (
                <>
                  <div><Label className="mb-1.5 block text-[13px]">Task title</Label><Input value={actionForm.title} onChange={(e) => setActionForm((p) => ({ ...p, title: e.target.value }))} placeholder="Follow up after consultation" data-testid="action-task-title" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="mb-1.5 block text-[13px]">Due date</Label><Input type="date" value={actionForm.due_date} onChange={(e) => setActionForm((p) => ({ ...p, due_date: e.target.value }))} data-testid="action-task-due" /></div>
                    <div><Label className="mb-1.5 block text-[13px]">Priority</Label>
                      <Select value={actionForm.priority} onValueChange={(v) => setActionForm((p) => ({ ...p, priority: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {actionType === "request_documents" && (
                <div><Label className="mb-1.5 block text-[13px]">Which documents?</Label><Textarea rows={3} value={actionForm.note} onChange={(e) => setActionForm((p) => ({ ...p, note: e.target.value }))} placeholder="e.g. POA, ID, recent care assessment, property deed" data-testid="action-docs-note" /></div>
              )}

              {actionType === "assign_partner" && (
                <>
                  <div><Label className="mb-1.5 block text-[13px]">Partner / vendor</Label>
                    <Select value={actionForm.partner_id} onValueChange={(v) => setActionForm((p) => ({ ...p, partner_id: v }))}>
                      <SelectTrigger data-testid="action-partner-select"><SelectValue placeholder="Choose partner" /></SelectTrigger>
                      <SelectContent className="max-h-[260px]">{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name || p.name || p.contact_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="mb-1.5 block text-[13px]">Referral type</Label><Input value={actionForm.referral_type} onChange={(e) => setActionForm((p) => ({ ...p, referral_type: e.target.value }))} placeholder="e.g. placement, home sale" data-testid="action-referral-type" /></div>
                </>
              )}

              {actionType === "revenue_opportunity" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="mb-1.5 block text-[13px]">Expected amount</Label><Input type="number" value={actionForm.amount} onChange={(e) => setActionForm((p) => ({ ...p, amount: e.target.value }))} placeholder="0" data-testid="action-revenue-amount" /></div>
                    <div><Label className="mb-1.5 block text-[13px]">Type</Label>
                      <Select value={actionForm.revenue_type} onValueChange={(v) => setActionForm((p) => ({ ...p, revenue_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{REVENUE_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="mb-1.5 block text-[13px]">Service lane</Label>
                    <Select value={actionForm.service_lane} onValueChange={(v) => setActionForm((p) => ({ ...p, service_lane: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {actionType === "follow_up_email" && (
                <>
                  <div><Label className="mb-1.5 block text-[13px]">To</Label><Input type="email" value={actionForm.to} onChange={(e) => setActionForm((p) => ({ ...p, to: e.target.value }))} placeholder="family@email.com" data-testid="action-email-to" /></div>
                  <div><Label className="mb-1.5 block text-[13px]">Subject</Label><Input value={actionForm.subject} onChange={(e) => setActionForm((p) => ({ ...p, subject: e.target.value }))} data-testid="action-email-subject" /></div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="block text-[13px]">Message</Label>
                      <button type="button" onClick={aiDraftEmail} disabled={aiBusy} className="text-xs text-[#9C1E22] hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="action-email-ai"><Sparkles size={13} /> {aiBusy ? "Drafting..." : "Draft with AI"}</button>
                    </div>
                    <Textarea rows={5} value={actionForm.body} onChange={(e) => setActionForm((p) => ({ ...p, body: e.target.value }))} data-testid="action-email-body" />
                  </div>
                  <p className="text-xs text-muted-foreground">We'll log this follow-up and open it in your email app to send.</p>
                </>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setActionType(null)}><X size={15} className="mr-1" /> Cancel</Button>
                <Button type="submit" disabled={actionBusy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="action-submit-button">{actionBusy ? "Working..." : "Confirm"}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
